import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

const MONTHS_PT = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
]

const COLORS = { cheias: "#9eadbd", ponta: "#b3c733", vazio: "#1787a8", producao: "#0ea5e9" }

/**
 * Ordem de LEITURA = ordem visual da barra, de cima para baixo. As <Bar> são
 * declaradas ao contrário (a 1ª fica na base), e o Recharts listava legenda e
 * tooltip pela ordem de declaração — Cheias primeiro, quando está em baixo
 * (induzia em erro, pedido do cliente).
 */
const READ_ORDER: Record<string, number> = { Vazio: 0, Ponta: 1, Cheias: 2, "Produção": 3 }
const LEGEND = [
  { id: "vazio", value: "Vazio", type: "square" as const, color: COLORS.vazio },
  { id: "ponta", value: "Ponta", type: "square" as const, color: COLORS.ponta },
  { id: "cheias", value: "Cheias", type: "square" as const, color: COLORS.cheias },
  { id: "producao", value: "Produção", type: "line" as const, color: COLORS.producao },
]

export interface ConsumptionChartProps {
  /**
   * Array com 12 entradas: { month:1..12, label, empty, peak, full, total }.
   * Pode ser passado tal como vem de `simulations.monthly_consumption_json`.
   */
  monthly_consumption: Array<{
    month: number
    label?: string
    empty: number
    peak: number
    full: number
    total: number
  }>
  /** Array de 12 valores em kWh — `simulations.monthly_production_json` */
  monthly_production: number[]
}

export function ConsumptionChart({
  monthly_consumption,
  monthly_production,
}: ConsumptionChartProps) {
  const data = MONTHS_PT.map((label, idx) => {
    const c = monthly_consumption[idx] ?? {
      empty: 0,
      peak: 0,
      full: 0,
      total: 0,
    }
    return {
      label,
      vazio: round(c.empty),
      ponta: round(c.peak),
      cheias: round(c.full),
      producao: round(monthly_production[idx] ?? 0),
    }
  })

  return (
    <div className="h-96 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
          <XAxis dataKey="label" fontSize={12} />
          <YAxis fontSize={12} unit=" kWh" width={70} />
          <Tooltip
            itemSorter={(item) => READ_ORDER[String(item.name)] ?? 9}
            formatter={(v: number, name: string) => [
              `${v.toLocaleString("pt-PT")} kWh`,
              name,
            ]}
            contentStyle={{
              background: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)",
              color: "var(--foreground)",
            }}
          />
          <Legend payload={LEGEND} />
          {/* Ordem de empilhamento pedida pelo cliente (igual ao gráfico do PDF,
              drawer solar_poupanca): a 1ª <Bar> fica na BASE.
              Cheias (base) — cinza
              Ponta (meio) — lima
              Vazio (topo) — azul-petróleo
              Produção (linha) — azul vivo destacado sobre as bars */}
          <Bar dataKey="cheias" stackId="cons" name="Cheias" fill={COLORS.cheias} />
          <Bar dataKey="ponta" stackId="cons" name="Ponta" fill={COLORS.ponta} />
          <Bar dataKey="vazio" stackId="cons" name="Vazio" fill={COLORS.vazio} />
          <Line
            type="monotone"
            dataKey="producao"
            stroke={COLORS.producao}
            strokeWidth={2.5}
            dot={{ r: 3, fill: COLORS.producao }}
            name="Produção"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

function round(n: number | string | null | undefined): number {
  const num = typeof n === "number" ? n : Number(n)
  if (!Number.isFinite(num)) return 0
  return Math.round(num)
}
